/**
 * The blockchain feed's interaction graph, drawn with the reader's catalog —
 * the overlay half, without the React Flow mapping.
 *
 * `feedGraph` merges the per-chain graphs the feed returns and `toGraph` maps
 * them onto React Flow elements — but nothing in that path ever resolved an
 * OVERLAY, so both feed surfaces (the Overview activity widget and the
 * explorer's Latest-blocks view) drew nodes labelled `0xabc…def`, with no tags
 * and no risk, while every other graph in the product drew the same addresses
 * with their catalog identity. The same address read one way on a transaction
 * page and another way on the landing page.
 *
 * This is the "second call" every other graph surface already makes
 * (`useScopedTransactionGraph`, `useWatchGraph`): once the graph lands, resolve
 * every on-chain node's overlay in ONE batch rather than a read per address.
 *
 * It hands the resolver back as well as the graph, because the resolver is the
 * only thing that can FREEZE what the graph was read with: an `owner_snapshot`
 * share link serves the owner's labels, entities and risk, and a surface that
 * resolves no overlays has nothing to put in one. See
 * `freezeCatalogSnapshotFromNodes` and `OnchainActivityGraph`.
 *
 * **Its own module, and that matters.** The mapping step lives next door in
 * `useFeedGraphElements`, which imports `toGraph` from the `@authe/tx-graph`
 * barrel — a single published bundle that imports `@xyflow/react` and `dagre` at
 * the top, so nothing can be shaken out of it. A module is the unit a bundler
 * keeps or drops, so the two hooks sharing one file would put the whole graph
 * engine into every chunk that resolves overlays — including the eager landing
 * chunk, whose activity panel defaults to the TABLE view.
 */
import * as React from "react";
import type { OverlayResolver } from "@authe/tx-graph";
import type { GraphNodeDTO, InteractionGraphDTO } from "@authe/fe-commons/models";
import { useCatalogApi } from "@/api/adapters";
import { useResolvedAddressOverlaysBatch, type OverlayRef } from "@/features/overlays";
import { feedGraph, type OnchainFeedResponse } from "./transpose";

export interface UseFeedGraphElementsOptions {
  /** Skip resolving while false (e.g. the Graph view isn't the active tab). */
  enabled?: boolean;
  /**
   * Also read each address's stored ROW, for a risk band an analyst set by hand.
   *
   * Off by default, and deliberately: these are the broadest feeds in the
   * product and they auto-refresh, so a read per address is a real cost — while
   * the half of risk that matters most here (a `Fake_Phishing` label, a
   * `phishing` tag) comes from the catalog and needs no row.
   *
   * The surface that mints an `owner_snapshot` link turns it on, because a band
   * somebody set by hand is precisely the owner's own judgement, which is what
   * such a link promises to carry.
   */
  includeRow?: boolean;
}

export interface FeedGraphOverlaysResult {
  /**
   * The feed's merged multichain graph, still as the BACKEND's shape.
   *
   * Stops one step short of React Flow elements on purpose. `toGraph` lives in
   * the `@authe/tx-graph` barrel, whose dist is a single module importing
   * `@xyflow/react` and `dagre` at the top — so a hook that maps elements
   * cannot be called from a surface whose graph is lazily loaded without
   * dragging the whole engine back into that surface's chunk. The overview's
   * activity panel is exactly that surface: its stored view defaults to the
   * TABLE, and it was shipping React Flow in the landing chunk to draw a graph
   * most readers never open.
   *
   * Whoever draws the graph maps it — see `useFeedGraphElements`, which is this
   * plus that one step, for a host already inside a graph chunk.
   */
  graph: InteractionGraphDTO;
  /**
   * The overlay seam these elements were drawn through, or `undefined` when
   * overlays are off for this surface — so a caller freezing a share snapshot
   * claims nothing rather than freezing an empty catalog.
   */
  resolveOverlay: OverlayResolver | undefined;
}

/** Shared empty result, so "no graph yet" keeps a stable identity across renders. */
const EMPTY_REFS: OverlayRef[] = [];

/**
 * The overlay half: the merged feed graph and the resolver it should be read
 * with, without the React Flow mapping.
 *
 * Split out of {@link useFeedGraphElements} so a surface that defers its canvas
 * can still resolve overlays — the batched catalog read is gated on `enabled`
 * either way, so nothing here costs a request the graph view was not going to
 * make.
 */
export function useFeedGraphOverlays(
  feed: OnchainFeedResponse | OnchainFeedResponse[] | undefined,
  options: UseFeedGraphElementsOptions = {},
): FeedGraphOverlaysResult {
  const { enabled = true, includeRow = false } = options;

  const merged = React.useMemo(() => feedGraph(feed), [feed]);

  const refs = React.useMemo<OverlayRef[]>(() => {
    if (!enabled) return EMPTY_REFS;
    const nodes = (merged.nodes ?? []) as GraphNodeDTO[];
    // Off-chain actors (a bridge, a DON) carry no address to attribute.
    return nodes
      .filter((node) => node.address)
      .map((node) => ({ address: node.address, chainId: node.chain_id ?? null }));
  }, [merged, enabled]);

  const catalog = useCatalogApi();
  const { resolveOverlay, active } = useResolvedAddressOverlaysBatch(refs, {
    // The feed IS transactions, so it reads at the transaction surface's gate —
    // the same one the explorer's scoped graph and a case's watch graph use.
    resource: "transaction",
    resolveLogoUrl: catalog.resolveLogoUrl,
    includeRow,
  });

  // `active` is the central config gate. Off, the graph must render exactly as
  // it did before, which means passing NO resolver rather than one that always
  // answers undefined — and the caller must be able to tell the difference.
  //
  // `enabled` is checked HERE as well as through the empty ref list. The batch
  // does turn itself off for no refs, but that is its internal rule, and this
  // hook's own promise — disabled means no resolver, so a share state freezes
  // nothing rather than an empty catalog — should not rest on it.
  const resolver = enabled && active ? resolveOverlay : undefined;

  return React.useMemo(
    () => ({ graph: merged, resolveOverlay: resolver }),
    [merged, resolver],
  );
}
